import React, { useEffect, useState, useCallback } from "react";
import { ShieldAlert, ShieldCheck, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Admin-only card that surfaces failed + blocked login attempts in a
 * rolling 24 h window. Reads `/api/admin/security/recent-failed-logins`
 * which was already in place before this widget shipped — the
 * backend groups events by client IP so an attack from one host
 * targeting many mobiles surfaces as a single high-attempts row.
 *
 * Design goals:
 *  - Green when quiet (zero attempts) so the widget fades into the
 *    background on normal days.
 *  - Amber ≥ 1 event, Rose ≥ 20 events — same threshold buckets we
 *    use elsewhere on the Overview so the coach reads all cards the
 *    same way.
 *  - Collapses the IP list by default; click "Show details" to expand.
 *
 * Only rendered for `role === "admin"` — coaches don't have a
 * meaningful action to take on this data, and the backend endpoint
 * requires admin anyway (guards against ambient 403s in the console).
 */
export default function SecurityWidget() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/admin/security/recent-failed-logins?hours=24");
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not load security stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) return null;

  const total = data?.total_events ?? 0;
  const byIp = data?.by_ip || [];
  const distinctIps = byIp.length;
  // Threshold buckets mirror what Squad Overview uses elsewhere
  const tone =
    total === 0 ? "emerald" : total >= 20 ? "rose" : "amber";
  const bgTone = {
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    amber:   "border-amber-500/30 bg-amber-500/5",
    rose:    "border-rose-500/40 bg-rose-500/10",
  }[tone];
  const iconTone = {
    emerald: "text-emerald-300",
    amber:   "text-amber-300",
    rose:    "text-rose-300",
  }[tone];
  const Icon = total === 0 ? ShieldCheck : ShieldAlert;

  return (
    <div
      className={`card p-5 md:p-6 border ${bgTone}`}
      data-testid="security-widget"
      data-no-print="true"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Icon size={22} className={iconTone} />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">
              Sign-in security · last 24 h
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5" data-testid="security-widget-headline">
              {loading && !data ? (
                "Loading…"
              ) : err ? (
                <span className="text-rose-300 text-sm">{err}</span>
              ) : total === 0 ? (
                "No failed sign-ins. All quiet."
              ) : (
                <>
                  <span className={iconTone}>{total}</span>{" "}
                  failed sign-in{total === 1 ? "" : "s"} from{" "}
                  <span className={iconTone}>{distinctIps}</span>{" "}
                  IP{distinctIps === 1 ? "" : "s"}
                </>
              )}
            </div>
            {!err && total > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                Blocked IPs and the mobiles they targeted are listed below. Use{" "}
                <span className="font-mono-data text-slate-300">Avatar → Manage users</span>{" "}
                if you need to review any account.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {total > 0 && (
            <button
              className="pill"
              onClick={() => setExpanded((v) => !v)}
              data-testid="security-widget-toggle"
              title={expanded ? "Hide per-IP breakdown" : "Show per-IP breakdown"}
            >
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              <span className="hidden sm:inline">{expanded ? "Hide" : "Show details"}</span>
            </button>
          )}
          <button
            className="pill"
            onClick={load}
            disabled={loading}
            data-testid="security-widget-refresh"
            title="Reload"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {expanded && total > 0 && (
        <div className="mt-4 overflow-x-auto" data-testid="security-widget-breakdown">
          <table className="w-full text-[12px] font-mono-data">
            <thead>
              <tr className="text-left text-slate-400 border-b border-[#1E304B]">
                <th className="py-1.5 pr-3">IP</th>
                <th className="py-1.5 pr-3 text-right">Attempts</th>
                <th className="py-1.5 pr-3 text-right">Blocks</th>
                <th className="py-1.5 pr-3 text-right">Mobiles targeted</th>
                <th className="py-1.5 pr-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {byIp.map((row) => (
                <tr key={row.ip} className="border-b border-[#0E1A2E] last:border-b-0">
                  <td className="py-1.5 pr-3 text-slate-200">{row.ip}</td>
                  <td className="py-1.5 pr-3 text-right text-amber-300">{row.attempts}</td>
                  <td className="py-1.5 pr-3 text-right text-rose-300">{row.blocks}</td>
                  <td className="py-1.5 pr-3 text-right text-slate-300">{row.distinct_mobiles}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{row.last_ts?.slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
